import React from 'react';
import { motion } from 'framer-motion';
import styles from './PrivacyPolicy.module.css';
import { 
  FiShield, FiLock, FiUserCheck, FiDatabase, 
  FiMail, FiGlobe, FiCreditCard, FiAlertCircle,
  FiCheckCircle, FiArrowRight, FiHome
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const pageVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  const sections = [
    {
      icon: FiShield,
      title: "Information We Collect",
      content: [
        "Personal identification information (Name, email address, phone number, etc.)",
        "Business information (Company name, position, industry, etc.)",
        "Technical data (IP address, browser type, device information)",
        "Usage data (Pages visited, time spent, features used)",
        "Communication preferences and feedback"
      ]
    },
    {
      icon: FiDatabase,
      title: "How We Use Your Information",
      content: [
        "To provide and maintain our services",
        "To notify you about changes to our services",
        "To provide customer support and respond to inquiries",
        "To gather analysis or valuable information to improve our services",
        "To monitor the usage of our services",
        "To detect, prevent and address technical issues"
      ]
    },
    {
      icon: FiLock,
      title: "Data Security",
      content: [
        "We implement industry-standard security measures to protect your data",
        "All data is encrypted using SSL/TLS technology",
        "Regular security audits and vulnerability assessments",
        "Access to personal data is restricted to authorized personnel only",
        "We use secure servers and firewalls to prevent unauthorized access"
      ]
    },
    {
      icon: FiUserCheck,
      title: "Your Rights",
      content: [
        "Right to access your personal data",
        "Right to rectify inaccurate data",
        "Right to request deletion of your data",
        "Right to restrict processing of your data",
        "Right to data portability",
        "Right to object to processing"
      ]
    },
    {
      icon: FiCreditCard,
      title: "Cookies & Tracking",
      content: [
        "We use cookies to enhance your browsing experience",
        "Essential cookies for website functionality",
        "Analytics cookies to understand user behavior",
        "You can control cookie preferences in your browser settings",
        "Third-party services may use their own tracking technologies"
      ]
    },
    {
      icon: FiGlobe,
      title: "Third-Party Services",
      content: [
        "We may share data with trusted third-party service providers",
        "All partners comply with data protection regulations",
        "We do not sell your personal information to third parties",
        "Third-party links are governed by their own privacy policies",
        "We use analytics services to improve our platform"
      ]
    }
  ];

  const lastUpdated = "January 15, 2024";

  return (
    <motion.div 
      className={styles.privacyPolicy}
      initial="hidden"
      animate="visible"
      variants={pageVariants}
    >
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <motion.div variants={itemVariants} className={styles.heroIcon}>
            <FiShield />
          </motion.div>
          <motion.h1 variants={itemVariants}>Privacy Policy</motion.h1>
          <motion.p variants={itemVariants}>
            Your privacy matters to us. Learn how we collect, use, and protect your information.
          </motion.p>
          <motion.div variants={itemVariants} className={styles.lastUpdated}>
            Last Updated: {lastUpdated}
          </motion.div>
        </div>
      </div>

      <div className={styles.container}>
        {/* Introduction */}
        <motion.div className={styles.introduction} variants={itemVariants}>
          <h2>Commitment to Privacy</h2>
          <p>
            At BrandVerse Technologies, we are committed to protecting your privacy and ensuring the security 
            of your personal information. This Privacy Policy explains how we collect, use, disclose, and 
            safeguard your information when you visit our website or use our services.
          </p>
          <div className={styles.commitmentBadge}>
            <FiCheckCircle />
            <span>GDPR Compliant</span>
            <FiCheckCircle />
            <span>CCPA Ready</span>
            <FiCheckCircle />
            <span>ISO 27001 Certified</span>
          </div>
        </motion.div>

        {/* Main Sections */}
        <div className={styles.sectionsGrid}>
          {sections.map((section, index) => (
            <motion.div 
              key={index}
              className={styles.sectionCard}
              variants={itemVariants}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className={styles.sectionIcon}>
                <section.icon />
              </div>
              <h3>{section.title}</h3>
              <ul className={styles.sectionList}>
                {section.content.map((item, idx) => (
                  <li key={idx}>
                    <FiArrowRight className={styles.listIcon} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Data Retention */}
        <motion.div className={styles.dataRetention} variants={itemVariants}>
          <h2>Data Retention Policy</h2>
          <p>
            We retain your personal information only for as long as necessary to fulfill the purposes 
            outlined in this Privacy Policy. We will retain and use your information to the extent necessary 
            to comply with our legal obligations, resolve disputes, and enforce our agreements.
          </p>
          <div className={styles.retentionGrid}>
            <div className={styles.retentionItem}>
              <strong>Active Accounts</strong>
              <p>Data retained for duration of account + 30 days</p>
            </div>
            <div className={styles.retentionItem}>
              <strong>Inactive Accounts</strong>
              <p>Data retained for 24 months after last activity</p>
            </div>
            <div className={styles.retentionItem}>
              <strong>Deleted Accounts</strong>
              <p>Data anonymized within 90 days of deletion request</p>
            </div>
          </div>
        </motion.div>

        {/* Children's Privacy */}
        <motion.div className={styles.childrenPrivacy} variants={itemVariants}>
          <h2>Children's Privacy</h2>
          <p>
            Our services are not intended for children under 13 years of age. We do not knowingly collect 
            personal information from children under 13. If you are a parent or guardian and you are aware 
            that your child has provided us with personal information, please contact us.
          </p>
        </motion.div>

        {/* International Users */}
        <motion.div className={styles.internationalUsers} variants={itemVariants}>
          <h2>International Users</h2>
          <p>
            Your information may be transferred to and maintained on computers located outside of your state, 
            province, country, or other governmental jurisdiction where data protection laws may differ. 
            By using our services, you consent to the transfer of your information to other countries.
          </p>
        </motion.div>

        {/* Contact Section */}
        <motion.div className={styles.contactSection} variants={itemVariants}>
          <h2>Questions & Concerns</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className={styles.contactInfo}>
            <div className={styles.contactCard}>
              <FiMail className={styles.contactIcon} />
              <h4>Email Us</h4>
              <a href="mailto:contact@brandversetech.com">contact@brandversetech.com</a>
            </div>
            <div className={styles.contactCard}>
              <FiAlertCircle className={styles.contactIcon} />
              <h4>Data Protection Officer</h4>
              <p>contact@brandversetech.com</p>
            </div>
          </div>
        </motion.div>

        {/* Back to Home Button */}
        <motion.div className={styles.backHome} variants={itemVariants}>
          <button onClick={() => navigate('/')} className={styles.backButton}>
            <FiHome />
            <span>Back to Home</span>
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default PrivacyPolicy;